'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchStore } from '@/stores/search-store';
import { almanakSearch } from '@/lib/search-client';
import type { SearchResult } from '@/lib/search-client';
import type { FilterTab } from '@/lib/search-categories';
import { isNaturalLanguageQuery, vertexSearch } from '@/lib/vertex-search-client';

type FocusZone = 'input' | 'filters' | 'results';

declare global {
  interface Window {
    _paq?: Array<Array<string | number | boolean>>;
  }
}

export function useCommandPalette() {
  const { isOpen, initialQuery, close } = useSearchStore();
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusZone, setFocusZone] = useState<FocusZone>('input');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shardError, setShardError] = useState(false);
  const [searchSource, setSearchSource] = useState<'local' | 'ai'>('local');
  const [correctedQuery, setCorrectedQuery] = useState<string | undefined>();
  const [pendingAiResults, setPendingAiResults] = useState<{
    results: SearchResult[];
    correctedQuery?: string;
  } | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const analyticsTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const aiAbortRef = useRef<AbortController | undefined>(undefined);
  const aiTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const queryGenRef = useRef(0); // generation counter to detect stale closures
  const activeFilterRef = useRef(activeFilter);

  // Load nav shard on first open
  useEffect(() => {
    if (!isOpen) return;
    setQuery(initialQuery);
    setActiveIndex(0);
    setActiveFilter('all');
    setFocusZone('input');
    setShardError(false);

    if (!almanakSearch.isLoaded('nav')) {
      setIsLoading(true);
      almanakSearch.loadShard('nav')
        .then(() => setIsLoading(false))
        .catch(() => {
          setIsLoading(false);
          setShardError(true);
        });
    }
  }, [isOpen, initialQuery]);

  // Keep ref in sync for use in async callbacks
  activeFilterRef.current = activeFilter;

  // Search when query or filter changes
  useEffect(() => {
    if (!isOpen) return;
    if (query.length < 2) {
      setResults([]);
      setSearchSource('local');
      setCorrectedQuery(undefined);
      return;
    }

    // Abort any pending AI search BEFORE debounce (fix: orphaned AbortController)
    aiAbortRef.current?.abort();
    aiAbortRef.current = undefined;

    // Bump generation counter to detect stale closures
    const gen = ++queryGenRef.current;

    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      // Always run MiniSearch first (instant)
      const hits = almanakSearch.search(query, {
        filter: activeFilter === 'all' ? undefined : activeFilter,
        limit: 20,
      });
      setResults(hits);
      setActiveIndex(0);
      setSearchSource('local');
      setCorrectedQuery(undefined);
      setPendingAiResults(null);

      // Lazy-load events shard if few nav results (fix: check generation before setState)
      if (hits.length < 5 && !almanakSearch.isLoaded('events')) {
        almanakSearch.loadShard('events').then(() => {
          if (queryGenRef.current !== gen) return; // stale — query changed
          const currentFilter = activeFilterRef.current;
          const moreHits = almanakSearch.search(query, {
            filter: currentFilter === 'all' ? undefined : currentFilter,
            limit: 20,
          });
          setResults(moreHits);
        }).catch(() => {
          // events shard failed, keep nav results
        });
      }

      // If NL query, also fire Vertex AI Search
      if (isNaturalLanguageQuery(query)) {
        const controller = new AbortController();
        aiAbortRef.current = controller;
        clearTimeout(aiTimeoutRef.current);
        aiTimeoutRef.current = setTimeout(() => controller.abort(), 4000);

        vertexSearch(query, { limit: 10, signal: controller.signal })
          .then(({ results: aiResults, correctedQuery: cq }) => {
            clearTimeout(aiTimeoutRef.current);
            // Verify this controller is still current AND generation matches
            if (aiAbortRef.current !== controller || queryGenRef.current !== gen) return;
            if (aiResults.length > 0) {
              // Stage AI results — show prompt instead of auto-swapping
              setPendingAiResults({ results: aiResults, correctedQuery: cq });
            }
          })
          .catch(() => {
            clearTimeout(aiTimeoutRef.current);
            // Timeout or error — keep MiniSearch results (already set)
          });
      }
    }, 50);

    return () => {
      clearTimeout(searchTimeoutRef.current);
      clearTimeout(aiTimeoutRef.current);
      aiAbortRef.current?.abort();
      aiAbortRef.current = undefined;
    };
  }, [query, activeFilter, isOpen]);

  // Matomo tracking (debounced)
  useEffect(() => {
    if (query.length < 2 || !isOpen) return;
    clearTimeout(analyticsTimeoutRef.current);
    analyticsTimeoutRef.current = setTimeout(() => {
      if (typeof window !== 'undefined' && window._paq) {
        const category = results.length > 0 ? results[0].pageType : 'none';
        window._paq.push(['trackSiteSearch', query, category, results.length]);
      }
    }, 800);
    return () => clearTimeout(analyticsTimeoutRef.current);
  }, [query, results, isOpen]);

  // Clamp active index
  useEffect(() => {
    if (activeIndex >= results.length && results.length > 0) {
      setActiveIndex(results.length - 1);
    }
  }, [results.length, activeIndex]);

  const queryRef = useRef(query);
  queryRef.current = query;
  const resultsRef = useRef(results);
  resultsRef.current = results;

  const handleEscape = useCallback(() => {
    if (queryRef.current.length > 0) {
      setQuery('');
      setFocusZone('input');
    } else {
      close();
    }
  }, [close]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentResults = resultsRef.current;
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          handleEscape();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusZone((z) => {
            if (z === 'results' || z === 'input') {
              if (currentResults.length > 0) {
                setActiveIndex((i) => Math.min(i + 1, currentResults.length - 1));
                return 'results';
              }
            }
            return z;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusZone((z) => {
            if (z === 'results') {
              setActiveIndex((prev) => {
                if (prev === 0) return prev;
                return Math.max(prev - 1, 0);
              });
              // Check if we should go back to input — use functional update
              // We read activeIndex via ref to decide
              return z; // stay in results, ArrowUp at 0 handled below
            }
            return z;
          });
          // Handle ArrowUp at index 0 → input (need to read current value)
          setActiveIndex((prev) => {
            if (prev === 0) {
              setFocusZone('input');
            }
            return prev;
          });
          break;
        case 'ArrowRight':
        case 'ArrowLeft':
          // handled by filter component when in filters zone
          break;
        case 'Tab':
          e.preventDefault();
          if (e.shiftKey) {
            setFocusZone((z) =>
              z === 'results' ? 'filters' : z === 'filters' ? 'input' : 'results'
            );
          } else {
            setFocusZone((z) =>
              z === 'input' ? 'filters' : z === 'filters' ? 'results' : 'input'
            );
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (focusZone === 'results' && currentResults[activeIndex]) {
            return currentResults[activeIndex]; // signal to navigate
          }
          if (focusZone === 'input' && currentResults.length > 0) {
            return currentResults[0];
          }
          break;
      }
      return null;
    },
    [focusZone, activeIndex, handleEscape],
  );

  /** Force MiniSearch re-run with the original (uncorrected) query */
  const forceOriginalQuery = useCallback(() => {
    setCorrectedQuery(undefined);
    setSearchSource('local');
    setPendingAiResults(null);
    // Re-run MiniSearch with the current query (bypass Vertex correction)
    const hits = almanakSearch.search(query, {
      filter: activeFilter === 'all' ? undefined : activeFilter,
      limit: 20,
    });
    setResults(hits);
    setActiveIndex(0);
  }, [query, activeFilter]);

  const acceptAiResults = useCallback(() => {
    if (!pendingAiResults) return;
    setResults(pendingAiResults.results);
    setSearchSource('ai');
    setCorrectedQuery(pendingAiResults.correctedQuery);
    setActiveIndex(0);
    setPendingAiResults(null);
  }, [pendingAiResults]);

  const dismissAiResults = useCallback(() => {
    setPendingAiResults(null);
  }, []);

  const retryShard = useCallback(() => {
    setShardError(false);
    setIsLoading(true);
    almanakSearch.loadShard('nav')
      .then(() => setIsLoading(false))
      .catch(() => {
        setIsLoading(false);
        setShardError(true);
      });
  }, []);

  return {
    isOpen,
    query,
    setQuery,
    activeFilter,
    setActiveFilter,
    activeIndex,
    setActiveIndex,
    focusZone,
    setFocusZone,
    results,
    isLoading,
    shardError,
    searchSource,
    correctedQuery,
    close,
    handleKeyDown,
    retryShard,
    pendingAiResults: !!pendingAiResults,
    acceptAiResults,
    dismissAiResults,
    forceOriginalQuery,
  };
}
